import { useCallback, useEffect, useRef } from 'react';
import * as chatApi from '../../api/chat';
import { isWindowVisible } from '../../utils/windowVisibility';

const HEARTBEAT_MS = 30_000;
const PONG_TIMEOUT_MS = 15_000;
const MAX_RETRIES = 12;
const MAX_BACKOFF_MS = 30_000;
// After the fast backoff is exhausted we DON'T give up — we drop to a slow
// keep-alive retry so the client self-heals from a prolonged server/proxy
// outage without a manual page refresh (F-019).
const KEEPALIVE_RETRY_MS = 30_000;

// Read token at connect-time so a token rotation in another tab is picked
// up on the next reconnect (F-018). Falls back to the prop for compatibility.
function readTokenFromStorage(): string | null {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

export function useChatWs(token: string | null, onEvent: (e: any) => void) {
  const wsRef = useRef<WebSocket | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout>>();
  const hbTimer = useRef<ReturnType<typeof setInterval>>();
  const pongTimer = useRef<ReturnType<typeof setTimeout>>();
  const onEventRef = useRef(onEvent);
  const retryCount = useRef(0);
  const tokenRef = useRef<string | null>(token);
  const explicitlyClosed = useRef(false);
  const disconnectedNotified = useRef(false);

  useEffect(() => {
    onEventRef.current = onEvent;
  });
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const connect = useCallback(() => {
    // F-018: re-read the token on every connect so a refresh from another
    // tab is honoured. Hook-prop wins when it's set; storage is the fallback.
    const liveToken = tokenRef.current || readTokenFromStorage();
    if (!liveToken) return;
    const current = wsRef.current;
    if (
      current &&
      (current.readyState === WebSocket.OPEN || current.readyState === WebSocket.CONNECTING)
    ) {
      return; // one socket per hook instance — never stack a second connect
    }

    explicitlyClosed.current = false;
    // Browser clients authenticate with the query token. Do not also offer the
    // JWT as a WebSocket subprotocol: a server must echo the selected protocol,
    // and long echoed JWT headers can make production proxies return 502.
    const ws = new WebSocket(chatApi.getChatWsUrl(liveToken));
    wsRef.current = ws;

    const clearPongWatchdog = () => {
      clearTimeout(pongTimer.current);
      pongTimer.current = undefined;
    };
    const armPongWatchdog = () => {
      clearPongWatchdog();
      pongTimer.current = setTimeout(() => {
        if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      }, PONG_TIMEOUT_MS);
    };

    ws.onopen = () => {
      // A socket that lost the race to a newer one (focus/online revive while
      // a retry was still connecting) must not start a second heartbeat loop
      // or deliver a second copy of every frame: close it and stay quiet.
      if (wsRef.current !== ws) {
        try { ws.close(); } catch { /* noop */ }
        return;
      }
      retryCount.current = 0;
      disconnectedNotified.current = false;
      clearTimeout(retryTimer.current);
      // Consumers with their own durable state (calls, unread recovery, etc.)
      // need an explicit signal on every successful initial open/reconnect.
      try {
        onEventRef.current({ type: 'ws_connected' });
      } catch {
        /* one recovery consumer must not break socket setup */
      }
      // `visible` lets the server flip a minimized client to 'away' even if the
      // one-shot presence push was lost.
      const beat = () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'heartbeat', visible: isWindowVisible() }));
          armPongWatchdog();
        }
      };
      // Re-assert the true window state IMMEDIATELY on every (re)connect — don't
      // wait a full interval. A reconnect (network blip, backend redeploy) makes
      // the server mark us 'online' again, and a still-minimized client won't
      // re-send its one-shot 'away' frame; this opening beat carries the real
      // window state so the server pulls us back to 'away' at once instead of
      // showing 'online' to peers until the next beat.
      beat();
      hbTimer.current = setInterval(beat, HEARTBEAT_MS);
    };

    ws.onmessage = (e) => {
      if (wsRef.current !== ws) return; // superseded socket — ignore its frames
      try {
        const event = JSON.parse(e.data);
        if (event?.type === 'pong') {
          clearPongWatchdog();
        }
        onEventRef.current(event);
      } catch {
        /* malformed frame — ignore */
      }
    };

    ws.onclose = (ev) => {
      // Only the live socket owns the heartbeat, the pong watchdog and the
      // reconnect schedule. A stale socket closing late (after a revive
      // already opened a new one) previously cleared the NEW socket's
      // heartbeat and queued an extra connect — the origin of duplicate
      // sockets and orphaned heartbeat intervals.
      if (wsRef.current !== ws) return;
      clearInterval(hbTimer.current);
      clearPongWatchdog();
      if (explicitlyClosed.current) return;

      // F-019: after the fast backoff is exhausted, notify the UI ONCE, then
      // keep a slow keep-alive retry instead of giving up. A dead socket that
      // never retries is the root cause of "I had to refresh to see messages":
      // reconnecting re-runs the server 'connected' → catch-up sync, so missed
      // messages are recovered automatically without user action.
      if (retryCount.current >= MAX_RETRIES) {
        if (!disconnectedNotified.current) {
          disconnectedNotified.current = true;
          try {
            onEventRef.current({ type: 'ws_disconnected', code: ev.code });
          } catch {
            /* noop */
          }
        }
        retryTimer.current = setTimeout(connect, KEEPALIVE_RETRY_MS);
        return;
      }
      // 4001 = unauthenticated (server side). Don't tight-loop with a stale
      // token — let the auth layer refresh and bounce us via re-mount.
      if (ev.code === 4001) {
        try {
          onEventRef.current({ type: 'ws_unauthorized' });
        } catch {
          /* noop */
        }
        return;
      }

      const base = Math.min(1000 * 2 ** retryCount.current, MAX_BACKOFF_MS);
      // Full jitter — uniform in [base/2, base] avoids thundering-herd.
      const delay = Math.floor(base / 2 + Math.random() * (base / 2));
      retryCount.current += 1;
      retryTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => ws.close();
  }, []);

  useEffect(() => {
    connect();

    // F-018: reconnect immediately when the browser comes back online; tear
    // down on offline so we don't waste retries while the network is dead.
    const onOnline = () => {
      retryCount.current = 0;
      clearTimeout(retryTimer.current);
      if (
        !wsRef.current ||
        wsRef.current.readyState === WebSocket.CLOSED ||
        wsRef.current.readyState === WebSocket.CLOSING
      ) {
        connect();
      }
    };
    const onOffline = () => {
      clearTimeout(retryTimer.current);
      try {
        wsRef.current?.close();
      } catch {
        /* noop */
      }
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    // F-019: foregrounding the tab (becoming visible or regaining focus) is a
    // strong signal to re-establish a dropped socket at once rather than waiting
    // out the backoff. The reconnect re-runs the server catch-up sync, so the
    // user sees any messages that arrived while backgrounded — no refresh needed.
    const revive = () => {
      if (document.visibilityState === 'hidden') return;
      retryCount.current = 0;
      clearTimeout(retryTimer.current);
      if (
        !wsRef.current ||
        wsRef.current.readyState === WebSocket.CLOSED ||
        wsRef.current.readyState === WebSocket.CLOSING
      ) {
        connect();
      }
    };
    document.addEventListener('visibilitychange', revive);
    window.addEventListener('focus', revive);

    return () => {
      explicitlyClosed.current = true;
      clearTimeout(retryTimer.current);
      clearInterval(hbTimer.current);
      clearTimeout(pongTimer.current);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', revive);
      window.removeEventListener('focus', revive);
      try {
        wsRef.current?.close();
      } catch {
        /* noop */
      }
    };
  }, [connect]);

  return useCallback((data: object) => {
    const ws = wsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(data));
    return true;
  }, []);
}
